import { type Ref } from 'react';

import { EyeIcon, EyeOffIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface HeaderRowProps {
  headerKey: string;
  headerValue: string;
  isVisible: boolean;
  isLast: boolean;
  onKeyChange: (value: string) => void;
  onValueChange: (value: string) => void;
  onToggleVisibility: () => void;
  onAdd: () => void;
  onRemove: () => void;
  keyRef?: Ref<HTMLInputElement>;
}

export const HeaderRow = ({
  headerKey,
  headerValue,
  isVisible,
  isLast,
  onKeyChange,
  onValueChange,
  onToggleVisibility,
  onAdd,
  onRemove,
  keyRef,
}: HeaderRowProps) => {
  const { t } = useTranslation();

  return (
    <div className="flex items-center gap-2">
      <div className="flex flex-1">
        <div className="flex-1">
          <Input
            ref={keyRef}
            value={headerKey}
            onChange={(e) => onKeyChange(e.target.value)}
            placeholder={t('mcp.header_key')}
            autoComplete="off"
            data-1p-ignore
            data-lpignore="true"
            className="rounded-r-none border-r-0 focus-visible:relative focus-visible:z-10"
          />
        </div>
        <div className="flex-1">
          <div className="relative">
            <Input
              value={headerValue}
              onChange={(e) => onValueChange(e.target.value)}
              type={isVisible ? 'text' : 'password'}
              placeholder={t('mcp.header_value')}
              autoComplete="new-password"
              data-1p-ignore
              data-lpignore="true"
              className={cn('rounded-l-none', headerValue && 'pr-8')}
            />
            {headerValue && (
              <Button
                type="button"
                variant="ghost-neutral"
                size="icon-xss"
                className="absolute top-1/2 right-2 -translate-y-1/2"
                aria-label={isVisible ? t('common.hide') : t('common.show')}
                onClick={onToggleVisibility}
              >
                {isVisible ? <EyeOffIcon size={16} /> : <EyeIcon size={16} />}
              </Button>
            )}
          </div>
        </div>
      </div>
      {isLast ? (
        <Button
          type="button"
          variant="secondary-neutral"
          size="icon-lg"
          aria-label={t('common.add')}
          onClick={onAdd}
        >
          <PlusIcon size={16} />
        </Button>
      ) : (
        <Button
          type="button"
          variant="ghost-neutral"
          size="icon-lg"
          className="hover:text-destructive hover:[&_svg]:text-destructive"
          aria-label={t('common.remove')}
          onClick={onRemove}
        >
          <Trash2Icon size={16} />
        </Button>
      )}
    </div>
  );
};
